import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Video, Loader2, Download, Wand2, X, History, Trash2, Sparkles, Play, Clock, Maximize } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api from "../lib/api";
import { cn } from "../lib/utils";
import { useLanguage } from "../context/LanguageContext";

export default function VideoGenPage() {
  const { isRTL } = useLanguage();
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [showGallery, setShowGallery] = useState(false);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [duration, setDuration] = useState(4);
  const [size, setSize] = useState("1280x720");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    fetchGallery();
  }, []);

  // Progress simulation during generation
  useEffect(() => {
    let interval;
    if (loading) {
      setProgress(0);
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) return prev;
          return prev + Math.random() * 3;
        });
      }, 2000);
    } else {
      setProgress(0);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const fetchGallery = async () => {
    setLoadingGallery(true);
    try {
      const response = await api.get("/video/gallery");
      setGallery(response.data || []);
    } catch (error) {
      console.error("Failed to fetch gallery:", error);
    } finally {
      setLoadingGallery(false);
    }
  };

  const deleteFromGallery = async (videoId) => {
    try {
      await api.delete(`/video/gallery/${videoId}`);
      setGallery(prev => prev.filter(vid => vid.id !== videoId));
      toast.success("تم حذف الفيديو");
    } catch (error) {
      toast.error("خطأ في الحذف");
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;

    setLoading(true);
    setGeneratedVideo(null);

    try {
      const response = await api.post("/video/generate", {
        prompt: prompt.trim(),
        duration,
        size
      }, {
        timeout: 700000 // 11+ minutes timeout
      });

      if (response.data.video_base64) {
        setGeneratedVideo(`data:video/mp4;base64,${response.data.video_base64}`);
        fetchGallery();
        toast.success("تم توليد الفيديو بنجاح!");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || "خطأ في توليد الفيديو");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!generatedVideo) return;
    
    const link = document.createElement('a');
    link.href = generatedVideo;
    link.download = `helcoo-video-${Date.now()}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("تم تحميل الفيديو");
  };

  const clearAll = () => {
    setPrompt("");
    setGeneratedVideo(null);
  };

  const sizes = [
    { value: "1280x720", label: "HD عرضي", icon: "16:9" },
    { value: "720x1280", label: "عمودي", icon: "9:16" },
  ];

  const durations = [
    { value: 4, label: "4 ثواني", desc: "سريع" },
    { value: 8, label: "8 ثواني", desc: "متوسط" },
    { value: 12, label: "12 ثانية", desc: "طويل" },
  ];

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-6">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-3 pt-16 md:pt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-foreground flex items-center justify-center">
                <Video className="w-4 h-4 text-background" />
              </div>
              <div>
                <h1 className="text-base font-bold text-foreground">{isRTL ? "توليد الفيديو" : "Video Generation"}</h1>
                <p className="text-[10px] text-muted-foreground">HELCOO AI</p>
              </div>
            </div>
            
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={() => { setShowGallery(true); fetchGallery(); }}
              className="flex items-center gap-2 px-3 py-2 rounded-xl bg-muted text-foreground text-sm font-medium"
            >
              <History className="w-4 h-4" />
              <span>{isRTL ? "المعرض" : "Gallery"}</span>
              {gallery.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-md bg-foreground text-background text-xs">
                  {gallery.length}
                </span>
              )}
            </motion.button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-4">
            {/* Prompt Input */}
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="relative">
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="صف الفيديو الذي تريد توليده... مثال: قطة تلعب البيانو في نادٍ للجاز"
                  className="min-h-[120px] resize-none rounded-2xl border-border bg-muted/50 focus:bg-background text-foreground placeholder:text-muted-foreground p-4"
                  dir="rtl"
                  disabled={loading}
                />
                <span className="absolute bottom-3 left-3 text-xs text-muted-foreground">
                  {prompt.length}/500
                </span>
              </div>

              {/* Duration Selection */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">مدة الفيديو</label>
                <div className="grid grid-cols-3 gap-2">
                  {durations.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      onClick={() => setDuration(d.value)}
                      disabled={loading}
                      className={cn(
                        "py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center gap-1",
                        duration === d.value
                          ? "bg-foreground text-background"
                          : "bg-muted text-muted-foreground border border-border hover:bg-muted/80"
                      )}
                    >
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">{d.label}</span>
                      <span className="text-xs opacity-70">{d.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Size Selection */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">حجم الفيديو</label>
                <div className="grid grid-cols-3 gap-2">
                  {sizes.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setSize(s.value)}
                      disabled={loading}
                      className={cn(
                        "py-3 px-4 rounded-xl font-medium transition-all flex flex-col items-center gap-1",
                        size === s.value
                          ? "bg-foreground text-background"
                          : "bg-muted text-muted-foreground border border-border hover:bg-muted/80"
                      )}
                    >
                      <Maximize className="w-4 h-4" />
                      <span className="text-sm">{s.label}</span>
                      <span className="text-xs opacity-70">{s.icon}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <motion.button
                  type="submit"
                  disabled={!prompt.trim() || loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 py-4 rounded-2xl bg-foreground text-background font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      جاري التوليد...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5" />
                      توليد الفيديو
                    </>
                  )}
                </motion.button>
                
                {(prompt || generatedVideo) && !loading && (
                  <motion.button
                    type="button"
                    onClick={clearAll}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-4 rounded-2xl bg-muted border border-border text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                )}
              </div>
            </form>

            {/* Info Box */}
            <div className="p-4 rounded-2xl bg-muted/50 border border-border">
              <p className="text-sm text-muted-foreground text-center">
                توليد الفيديو قد يستغرق من 2-5 دقائق حسب المدة والجودة
              </p>
            </div>
          </div>

          {/* Result Section */}
          <div className="relative">
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="aspect-video rounded-2xl bg-muted border border-border flex flex-col items-center justify-center gap-4"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <Video className="w-12 h-12 text-muted-foreground" />
                  </motion.div>
                  <p className="text-muted-foreground">جاري توليد الفيديو...</p>
                  <p className="text-xs text-muted-foreground">قد يستغرق هذا بضع دقائق</p>
                  
                  {/* Progress Bar */}
                  <div className="w-64 h-2 bg-border rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-foreground"
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{Math.round(progress)}%</p>
                </motion.div>
              ) : generatedVideo ? (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="relative rounded-2xl overflow-hidden border border-border"
                >
                  <video
                    src={generatedVideo}
                    controls
                    autoPlay
                    loop
                    className="w-full aspect-video object-cover"
                  />
                  <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleDownload}
                      className="flex-1 py-3 rounded-xl bg-white/90 text-black font-medium flex items-center justify-center gap-2 backdrop-blur-sm"
                    >
                      <Download className="w-4 h-4" />
                      تحميل
                    </motion.button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="aspect-video rounded-2xl bg-muted/50 border-2 border-dashed border-border flex flex-col items-center justify-center gap-4"
                >
                  <Video className="w-16 h-16 text-muted-foreground/50" />
                  <p className="text-muted-foreground text-center">
                    الفيديو سيظهر هنا<br/>
                    <span className="text-sm">أدخل وصفاً وانقر توليد</span>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      <AnimatePresence>
        {showGallery && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowGallery(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-background rounded-3xl p-6 w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-foreground flex items-center gap-3">
                  <History className="w-6 h-6" />
                  معرض الفيديوهات
                </h2>
                <button
                  onClick={() => setShowGallery(false)}
                  className="p-2 rounded-full hover:bg-muted"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {loadingGallery ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : gallery.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>لا توجد فيديوهات محفوظة بعد</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {gallery.map((vid) => (
                      <motion.div
                        key={vid.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="relative group rounded-xl overflow-hidden border border-border"
                      >
                        <video
                          src={`data:video/mp4;base64,${vid.video_base64}`}
                          className="w-full aspect-video object-cover"
                          muted
                          onMouseEnter={(e) => e.target.play()}
                          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all">
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <p className="text-white text-xs line-clamp-2 mb-2">{vid.prompt}</p>
                            <div className="flex items-center gap-2 mb-2 text-white/70 text-xs">
                              <Clock className="w-3 h-3" />
                              <span>{vid.duration}ث</span>
                              <Maximize className="w-3 h-3 mr-2" />
                              <span>{vid.size}</span>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setGeneratedVideo(`data:video/mp4;base64,${vid.video_base64}`);
                                  setPrompt(vid.prompt);
                                  setShowGallery(false);
                                }}
                                className="flex-1 py-2 rounded-lg bg-white text-black text-xs font-medium flex items-center justify-center gap-1"
                              >
                                <Play className="w-3 h-3" />
                                تشغيل
                              </button>
                              <button
                                onClick={() => deleteFromGallery(vid.id)}
                                className="p-2 rounded-lg bg-red-500/80 text-white"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
